
'use client';

import { useState, useEffect, useMemo, Suspense } from 'react';
import { collection, getDocs, query, orderBy, doc, getDoc, onSnapshot, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Product, Category, StoreSettings, ProductVariant } from '@/lib/types';
import { Loader2, Search, ShoppingCart, Plus, Minus, Zap } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

function FastOrderContent() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [storeSettings, setStoreSettings] = useState<StoreSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedProductForVariants, setSelectedProductForVariants] = useState<Product | null>(null);

  const { cartItems, addToCart, updateQuantity } = useCart();
  const searchParams = useSearchParams();
  const categoryParam = searchParams.get('category');

  useEffect(() => {
    sessionStorage.setItem('checkout_source', 'fastorder');
    setIsLoading(true);

    // SÊNIOR: Limite de 200 produtos para garantir que o menu carregue instantaneamente
    const qProducts = query(collection(db, 'products'), orderBy('position', 'asc'), limit(200));
    const qCategories = query(collection(db, 'categories'), orderBy('position', 'asc'));

    const unsubscribeProducts = onSnapshot(qProducts, (snapshot) => {
        const fetchedProducts = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Product));
        const availableProducts = fetchedProducts.filter(p => {
           const stock = p.variants && p.variants.length > 0 
             ? p.variants.reduce((acc, v) => acc + Math.max(0, Number(v.stock || 0)), 0)
             : Math.max(0, Number(p.stock || 0));
           return stock > 0;
        });
        setProducts(availableProducts);
        setIsLoading(false);
    });

    const unsubscribeCategories = onSnapshot(qCategories, (snapshot) => {
        const fetchedCategories = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Category));
        setCategories(fetchedCategories);
    });

    getDoc(doc(db, 'settings', 'store')).then(snap => {
        if (snap.exists()) setStoreSettings(snap.data() as StoreSettings);
    });

    if (categoryParam) {
        setActiveTab(categoryParam);
    }

    return () => {
        unsubscribeProducts();
        unsubscribeCategories();
    };
  }, [categoryParam]);

  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    if (activeTab !== 'all') {
      const categoryName = categories.find(c => c.id === activeTab)?.name;
      if (categoryName) {
        filtered = filtered.filter(p => p.category === categoryName);
      }
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(term) || 
        p.brand?.toLowerCase().includes(term) ||
        p.category.toLowerCase().includes(term)
      );
    }

    const categoryOrderMap = new Map(categories.map((cat, index) => [cat.name, index]));

    return filtered.sort((a, b) => {
      const orderA = categoryOrderMap.get(a.category) ?? 999;
      const orderB = categoryOrderMap.get(b.category) ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      return (a.position || 0) - (b.position || 0);
    });
  }, [products, categories, activeTab, searchTerm]);

  const cartTotal = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  }, [cartItems]);

  const cartCount = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + item.quantity, 0);
  }, [cartItems]);

  const handleVariantAdd = (product: Product, variant: ProductVariant) => {
    addToCart(product, 1, variant);
    setSelectedProductForVariants(null);
  };

  if (isLoading) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 pb-32 max-w-2xl">
      <div className="sticky top-16 z-30 bg-background/95 backdrop-blur-sm pt-2 pb-4 space-y-4">
        <div className="flex items-center gap-2">
            <Zap className="text-primary h-6 w-6 fill-primary" />
            <h1 className="text-2xl font-bold font-headline">Cardápio Wolf</h1>
        </div>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input 
            placeholder="Buscar sabores, marcas..." 
            className="pl-10 h-11 text-base bg-muted/50 border-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <ScrollArea className="w-full whitespace-nowrap">
            <div className="flex gap-2 pb-3">
                <Button 
                    variant={activeTab === 'all' ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-full h-9 px-5"
                    onClick={() => setActiveTab('all')}
                >
                    Todos
                </Button>
                {categories.map(cat => (
                    <Button 
                        key={cat.id}
                        variant={activeTab === cat.id ? 'default' : 'outline'}
                        size="sm"
                        className="rounded-full h-9 px-5 whitespace-nowrap"
                        onClick={() => setActiveTab(cat.id)}
                    >
                        {cat.name}
                    </Button>
                ))}
            </div>
            <ScrollBar orientation="horizontal" className="h-1.5" />
        </ScrollArea>
      </div>

      <div className="grid gap-2 mt-4">
        {filteredProducts.map(product => {
          const cartItem = cartItems.find(item => item.id === product.id);
          const quantity = cartItem?.quantity || 0;
          const hasVariants = product.variants && product.variants.length > 0;
          const hasDiscount = product.originalPrice && product.originalPrice > product.price;

          const consolidatedStock = hasVariants 
            ? product.variants!.reduce((acc, v) => acc + Math.max(0, Number(v.stock || 0)), 0)
            : Math.max(0, Number(product.stock || 0));

          return (
            <div key={product.id} className="flex gap-4 p-4 border rounded-xl bg-card hover:bg-muted/30 transition-all duration-300">
                <Link href={`/product/${product.slug || product.id}`} className="flex-shrink-0">
                    <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-muted">
                        <Image 
                            src={product.imageUrl || 'https://placehold.co/150x150.png'} 
                            alt={product.name}
                            fill
                            className="object-cover"
                            loading="lazy"
                        />
                        {product.displayTags && product.displayTags.length > 0 && (
                            <Badge 
                                className="absolute top-0 left-0 rounded-none rounded-br-md text-[0.5rem] py-0 px-1 font-bold z-10"
                                style={{ backgroundColor: product.displayTagBgColor || '#000000', color: product.displayTagTextColor || '#FFFFFF' }}
                            >
                                {product.displayTags[0]}
                            </Badge>
                        )}
                    </div>
                </Link>
                
                <div className="flex-1 flex flex-col justify-between">
                  <div className="space-y-0.5">
                    <Link href={`/product/${product.slug || product.id}`}>
                        <h3 className="font-bold text-[0.9rem] leading-tight line-clamp-1">{product.name}</h3>
                    </Link>
                    <p className="text-[0.65rem] text-muted-foreground uppercase tracking-widest">{product.brand}</p>
                  </div>
                  
                  <div className="flex justify-between items-center mt-2">
                    <div className="flex flex-col">
                        {hasDiscount && <span className="text-[0.65rem] text-muted-foreground line-through">R$ {product.originalPrice?.toFixed(2)}</span>}
                        <p className="text-primary font-bold text-base">R$ {product.price.toFixed(2)}</p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        {hasVariants ? (
                            <Button size="sm" variant="outline" className="h-8 rounded-lg text-[0.7rem] font-bold" onClick={() => setSelectedProductForVariants(product)}>OPÇÕES</Button>
                        ) : (
                            quantity > 0 ? (
                                <div className="flex items-center bg-primary text-primary-foreground rounded-lg h-8 px-1">
                                    <Button variant="ghost" size="icon" className="h-6 w-6 hover:bg-white/20" onClick={() => updateQuantity(product.id, quantity - 1)}><Minus className="h-3 w-3" /></Button>
                                    <span className="w-7 text-center text-sm font-bold">{quantity}</span>
                                    <Button variant="ghost" size="icon" className="h-6 w-6 hover:bg-white/20" onClick={() => updateQuantity(product.id, quantity + 1)} disabled={quantity >= consolidatedStock}><Plus className="h-3 w-3" /></Button>
                                </div>
                            ) : (
                                <Button size="sm" variant="outline" className="h-8 rounded-lg px-4 text-[0.7rem] font-bold border-primary text-primary hover:bg-primary" onClick={() => addToCart(product, 1)}>ADD</Button>
                            )
                        )}
                    </div>
                  </div>
                </div>
            </div>
          );
        })}
      </div>

      {filteredProducts.length === 0 && (
          <div className="text-center py-24 opacity-50">
              <ShoppingCart className="h-10 w-10 mx-auto mb-2" />
              <p>Nenhum produto encontrado.</p>
          </div>
      )}

      {cartCount > 0 && (
        <div className="fixed bottom-6 left-0 right-0 px-4 z-50 animate-in fade-in slide-in-from-bottom-4">
            <Button asChild size="lg" className="w-full h-14 bg-primary text-primary-foreground shadow-2xl rounded-xl flex items-center justify-between px-6 hover:bg-primary/95 transition-transform active:scale-95">
                <Link href="/cart" className="w-full flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-white/20 h-7 min-w-[1.75rem] flex items-center justify-center rounded text-xs font-bold px-1.5">{cartCount}</div>
                        <span className="font-bold text-sm">Ver Carrinho</span>
                    </div>
                    <span className="font-bold text-base">R$ {cartTotal.toFixed(2)}</span>
                </Link>
            </Button>
        </div>
      )}

      <Dialog open={!!selectedProductForVariants} onOpenChange={() => setSelectedProductForVariants(null)}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-t-2xl sm:rounded-lg">
          {selectedProductForVariants && (
            <div className="flex flex-col">
              <div className="relative w-full h-48">
                <Image src={selectedProductForVariants.imageUrl || 'https://placehold.co/400x400.png'} alt={selectedProductForVariants.name} fill className="object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                   <h2 className="text-xl font-bold text-white">{selectedProductForVariants.name}</h2>
                   <p className="text-white/80 text-xs mt-1 uppercase tracking-widest">{selectedProductForVariants.brand}</p>
                </div>
              </div>
              <div className="p-4 space-y-4 bg-card">
                <h3 className="font-bold text-sm text-muted-foreground uppercase tracking-wider">Escolha uma opção</h3>
                <div className="grid gap-2 max-h-[40vh] overflow-y-auto pr-1">
                  {selectedProductForVariants.variants?.filter(v => Math.max(0, Number(v.stock || 0)) > 0).map(variant => (
                    <button key={variant.id} onClick={() => handleVariantAdd(selectedProductForVariants, variant)} className="flex items-center justify-between p-3 rounded-xl border hover:border-primary/50 hover:bg-primary/5 transition-all text-left">
                      <span className="font-semibold text-sm">{variant.options.name}</span>
                      <span className="font-bold text-primary text-sm">R$ {variant.price.toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="p-4 border-t bg-muted/30">
                 <Button variant="ghost" className="w-full" onClick={() => setSelectedProductForVariants(null)}>Cancelar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function FastOrderPage() {
  return (
    <Suspense fallback={<div className="flex h-[80vh] items-center justify-center"><Loader2 className="h-12 w-12 animate-spin text-primary" /></div>}>
      <FastOrderContent />
    </Suspense>
  );
}
